import { Action, ActionClientContext, InvalidActionStateError } from "libs/actions";
import { throttleAsyncFn } from "libs/promise-utils";
import { createRecordMapFromPointersWithRecords } from "libs/schema";
import { applyTransaction, getOperationPointers, op } from "libs/transaction";
import { RequiredParameters } from "libs/type-helpers";
import { RecordLoaderApi } from "libs/database";
import { ClientEnvironment } from "~/environment/ClientEnvironment";
import { EnqueueOptions } from "~/environment/ActionQueue";
import { createRecordLoader } from "~/environment/RecordLoader";
import { uniqWith } from "lodash-comms";
import { isEqual } from "libs/predicates";
import { toast } from "~/environment/toast-service";

/* -------------------------------------------------------------------------------------------------
 *  withActionContext
 * -------------------------------------------------------------------------------------------------
 */

export interface ActionExecutor<R> {
  forward: (props: { isRedo: boolean }) => R;
  backward?: () => unknown;
}

export function withActionContext<R>(
  fn: (context: ActionClientContext, environment: ClientEnvironment, props: undefined) => Promise<ActionExecutor<R>>,
): (environment: ClientEnvironment) => Promise<Awaited<R>>;
export function withActionContext<T extends Record<string, any> | undefined, R>(
  fn: (context: ActionClientContext, environment: ClientEnvironment, props: T) => Promise<ActionExecutor<R>>,
): (environment: ClientEnvironment, props: T) => Promise<Awaited<R>>;
export function withActionContext<T extends Record<string, any> | undefined = undefined>(
  fn: (context: ActionClientContext, environment: ClientEnvironment, props: T) => Promise<ActionExecutor<unknown>>,
) {
  return async (environment: ClientEnvironment, props: T) => {
    using disposable = environment.isPendingUpdate.add();
    let undoRedoStackUpdated = false;

    try {
      const context = createActionClientContext(environment);
      const executor = await fn(context, environment, props);

      const { forward, backward } = executor;

      if (backward) {
        const { undoRedo } = environment;

        // Currently we only support undoing the most recent change. This is a user experience decision, as allowing undoing
        // more actions can get confusing if you accidentally undo an action which was taken on a previous page.
        undoRedo.undoStack = [
          {
            forward: () => forward({ isRedo: true }),
            backward,
          },
        ];

        undoRedo.redoStack = [];
        undoRedo.resetUndoWindow();
        undoRedoStackUpdated = true;
      }

      return await forward({ isRedo: false });
    } catch (error) {
      environment.logger.error(
        { error },
        `[withActionContext] ${error instanceof Error ? error.message : "unknown error"}`,
      );

      if (undoRedoStackUpdated) {
        environment.undoRedo.undoStack = [];
        environment.undoRedo.redoStack = [];
        environment.undoRedo.resetUndoWindow();
      }

      if (error instanceof InvalidActionStateError && error.userMessage !== "SUPPRESS") {
        toast("vanilla", {
          subject: error.userMessage,
          description: error.userHint,
        });
      }

      throw error;
    }
  };
}

/* -------------------------------------------------------------------------------------------------
 *  write
 * -------------------------------------------------------------------------------------------------
 */

export type WriteProps = {
  action: Action<unknown>;
  onOptimisticWrite?: () => void;
};

export async function write(
  environment: ClientEnvironment,
  props: WriteProps,
  /**
   * Internal use. Instead use the exported `debouncedWrite` function.
   */
  _debounce?: EnqueueOptions["debounce"],
): Promise<void> {
  if (!props.action.operations.length) return;

  optimisticWrite(environment, props.action);

  props.onOptimisticWrite?.();

  await enqueueAction(environment, props.action, { debounce: _debounce });
}

/* -------------------------------------------------------------------------------------------------
 *  debouncedWrite
 * -------------------------------------------------------------------------------------------------
 */

// The ActionQueue already has logic to debounce enqueued actions if we provide a debounce key.
// The problem is that, generally, when constructing a action we need to perform async operations to
// fetch documents. If we don't debounce these async operations, then potentially we call our function to
// construct the action multiple times, each time an async process starts to get the related documents,
// those processes can resolve out of order resulting in the actions getting enqueued (with the
// debounce option) out of order. To fix this we throttle enqueuing debounced actions until the async
// operation to fetch the documents has resolved. Notably, we don't want to wait for the debounced action
// to be committed on the server though.
//
// Note that `debouncedWrite` could have unexpected interactions with the ActionQueue. E.g. If
// debouncedWriteInner is pending that means a action is still being constructed but hasn't been added
// to the ActionQueue. Calling ActionQueue#flush would thus not affect that action. For this
// reason, the debouncedWrite function has a flush method that can be used to wait for all debounced actions
// to finish processing.
export async function debouncedWrite<T extends Record<string, any> | undefined, R>(
  environment: ClientEnvironment,
  /** If debounce is undefined then this is basically the same as calling the `write` function. */
  debounce: EnqueueOptions["debounce"],
  fn: () => Promise<WriteProps | undefined>,
): Promise<void> {
  if (debounce) {
    const { wrappedPromise } = await debouncedWriteInner(environment, debounce, fn);
    return wrappedPromise;
  }

  const writeProps = await fn();
  if (!writeProps) return;
  return module.write(environment, writeProps);
}

const debouncedWriteInner = throttleAsyncFn(
  async (...args: RequiredParameters<typeof debouncedWrite>) => {
    const [environment, debounce, fn] = args;
    const writeProps = await fn();
    // The promise returned by `write` will resolve when the action has been committed on the server.
    // We only want to throttle enqueueing the debounced action. We don't want to wait for the
    // action to be committed. As such, we return the promise wrapped in an object so that
    // the throttle logic doesn't await it's resolution.
    const wrappedPromise = writeProps ? module.write(environment, writeProps, debounce) : Promise.resolve();
    return { wrappedPromise };
  },
  (_, debounce) => debounce.key,
);

debouncedWrite.promiseCache = debouncedWriteInner.promiseCache;

/**
 * Waits for the provided keys to finish being added to the ActionQueue. If no keys are provided then it waits for all
 * debounced actions.
 */
debouncedWrite.flush = async (keys?: string[]) => {
  if (!keys) {
    keys = Array.from(debouncedWrite.promiseCache.keys());
  }

  await Promise.all(
    keys.map(async (key) => {
      let cached = debouncedWrite.promiseCache.get(key);

      while (cached?.executingPromise) {
        await cached.executingPromise;
        cached = debouncedWrite.promiseCache.get(key);
      }
    }),
  );
};

/* -------------------------------------------------------------------------------------------------
 *  undo
 * -------------------------------------------------------------------------------------------------
 */

export async function undo(environment: ClientEnvironment) {
  const { undoRedo } = environment;

  if (undoRedo.running) return;

  const undoEntry = undoRedo.undoStack.pop();
  if (!undoEntry) return;

  try {
    undoRedo.running = true;

    await undoEntry.backward();

    undoRedo.redoStack.push(undoEntry);

    undoRedo.resetUndoWindow();
  } catch (error) {
    environment.logger.error({ error }, `[undo] ${error instanceof Error ? error.message : "unknown error"}`);

    environment.undoRedo.undoStack = [];
    environment.undoRedo.redoStack = [];
    environment.undoRedo.resetUndoWindow();

    if (error instanceof InvalidActionStateError && error.userMessage !== "SUPPRESS") {
      toast("vanilla", {
        subject: error.userMessage,
        description: error.userHint,
      });
    }
  } finally {
    undoRedo.running = false;
  }
}

/* -------------------------------------------------------------------------------------------------
 *  redo
 * -------------------------------------------------------------------------------------------------
 */

export async function redo(environment: ClientEnvironment) {
  const { undoRedo } = environment;

  if (undoRedo.running) return;

  const redoEntry = undoRedo.redoStack.pop();
  if (!redoEntry) return;

  try {
    undoRedo.running = true;

    await redoEntry.forward();

    undoRedo.undoStack.push(redoEntry);

    undoRedo.resetUndoWindow();
  } catch (error) {
    environment.logger.error({ error }, `[redo] ${error instanceof Error ? error.message : "unknown error"}`);

    environment.undoRedo.undoStack = [];
    environment.undoRedo.redoStack = [];
    environment.undoRedo.resetUndoWindow();

    if (error instanceof InvalidActionStateError && error.userMessage !== "SUPPRESS") {
      toast("vanilla", {
        subject: error.userMessage,
        description: error.userHint,
      });
    }
  } finally {
    undoRedo.running = false;
  }
}

/* -----------------------------------------------------------------------------------------------*/

/**
 * This function synchronously writes the action to the in-memory database and then asynchronously
 * writes it to the persisted database. The returned promise will resolve when the action has finished
 * being applied to both databases.
 */
// Important! Should be a sync function. See comments below.
function optimisticWrite(
  environment: Pick<ClientEnvironment, "db" | "writeRecordMap" | "logger" | "auth">,
  action: Action<unknown>,
): Promise<void> {
  const { db, writeRecordMap, auth } = environment;

  const transaction = op.transaction({
    authorId: auth.getAndAssertCurrentUserId(),
    label: action.name,
    operations: action.operations,
  });

  // Get cached records for this write.
  const pointers = uniqWith(transaction.operations.flatMap(getOperationPointers), isEqual);
  const [pointerWithRecords] = db.getRecords(pointers, { includeSoftDeletes: true });
  const recordMap = createRecordMapFromPointersWithRecords(pointerWithRecords);

  const currentTimestamp = new Date().toISOString();

  // Optimistic update.
  applyTransaction({
    recordMap,
    transaction,
    currentTimestamp,
    isServer: false,
    useActionVersioning: true,
  });

  environment.logger.debug({ recordMap, transaction, action }, `[${transaction.label}] optimisticWrite`);

  // Note that writeRecordMap itself is not an async function but it returns a promise.
  // It synchronously updates the in memory cache and async updates the persisted database.
  // Similarly, we want this optimistic write to be synchronous on the in-memory database
  // so we don't want to make this an async function.
  return writeRecordMap(recordMap).catch((error) => {
    environment.logger.error(
      { transaction, error, action },
      // The in-memory database will error synchronously so if we've caught a rejected promise here
      // that means it came from the persisted database.
      `[${transaction.label}] error applying optimistic write to persisted db`,
    );
  });
}

/* -----------------------------------------------------------------------------------------------*/

async function enqueueAction(
  environment: Pick<ClientEnvironment, "actionQueue" | "logger">,
  action: Action<unknown>,
  options?: { debounce?: EnqueueOptions["debounce"] },
) {
  const { actionQueue, logger } = environment;

  try {
    await actionQueue.enqueue(action, options);
  } catch (error) {
    logger.error({ ...action, error }, `[enqueueAction] error`);
  }
}

/* -------------------------------------------------------------------------------------------------
 *  Utility Functions
 * -------------------------------------------------------------------------------------------------
 */

function createActionClientContext(environment: ClientEnvironment): ActionClientContext {
  return {
    environment: {
      loader: createRecordLoader(environment, { defaultFetchStrategy: "cache-first" }) as unknown as RecordLoaderApi,
      logger: environment.logger,
    },
    currentUserId: environment.auth.getAndAssertCurrentUserId(),
    type: "client",
  };
}

// Used for testing purposes
export const module = {
  write,
  debouncedWrite,
};
